// noinspection DuplicatedCode
// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev
import {Loader} from '@googlemaps/js-api-loader';
// eslint-disable-next-line import/extensions
import {MarkerClusterer, SuperClusterAlgorithm} from '@googlemaps/markerclusterer/dist/index.umd.js';
import {Runner} from './models/runner';
import PathProvider from './providers/path';
import {
    RUNS,
    WAYPOINTS,
    Waypoint,
    findWaypointForPosition, RUN_HALF,
} from './models/waypoint';
import RunnerIconProvider from './providers/runner-icon';
import initRunnersStore, {RunnersStore} from './store/runners';
import RunProvider, {LANGUAGE_DE} from './providers/run';

export type WaypointMarker = {waypoint: Waypoint, marker: google.maps.Marker};

class RaceMap {
    googleMap: google.maps.Map;

    pathProvider: PathProvider;

    runnerIconProvider: RunnerIconProvider;

    run = RUN_HALF;

    runnerMarkers = new Map<string, google.maps.Marker>();

    runnerMarkersLock = new Map<string, boolean>();

    waypointMarkers: Array<WaypointMarker> = [];

    runPaths = new Map<string, google.maps.Polyline>();

    clusterer: MarkerClusterer;

    geolocationMarker: google.maps.Marker;

    FOLLOW_DISABLED = 'disabled';

    FOLLOW_RUNNER = 'runner';

    FOLLOW_GEOLOCATION = 'geolocation';

    follow = this.FOLLOW_DISABLED;

    showGeolocationButton = true;

    lastGeolocationPosition = null;

    private readonly mapElement: HTMLElement;

    private toggleFollowRunnerButton: Element;

    private runProvider: RunProvider;

    private favoriteRunners: RunnersStore;

    private runners: Array<Runner>;

    private updateRunnerMarkersInterval: ReturnType<typeof setInterval>;

    constructor(GoogleMap: typeof google.maps.Map) {
        this.mapElement = document.querySelector('.js-race-tracking-map');
        if (!this.mapElement) {
            return;
        }

        this.pathProvider = new PathProvider();
        this.runProvider = RunProvider.getInstance();

        this.runProvider.setLanguage(window.config.language || LANGUAGE_DE);

        this.favoriteRunners = initRunnersStore();

        this.googleMap = new GoogleMap(this.mapElement, {
            center: {
                lat: 50.938,
                lng: 6.956,
            },
            minZoom: 10,
            maxZoom: 18,
            disableDefaultUI: true,
            zoomControl: false,
        });

        this.toggleFollowRunnerButton = document.querySelector('.js-race-tracking-activate-follow-runner');
    }

    async init() {
        if (!this.googleMap) {
            throw new Error('No Map element found');
        }

        // Follow deaktivieren bei Drag Start
        this.googleMap.addListener('dragstart', () => {
            this.toggleFollowRunner(false);
        });

        this.addPaths();
        this.addWaypoints();
        this.setBounds();

        await this.initRunners(() => {
            this.mapElement.classList.remove('race-tracking__map--disabled');

            this.initGeolocationMarker();
        });
    }

    async initRunners(initMap: () => void) {
        this.runners = this.favoriteRunners.getState().favoriteRunners;
        const hasRunners = this.runners.length > 0;

        if (hasRunners) {
            initMap();
        }

        await this.matchRunnerMarkersWithRunners();
        this.updateRunnerMarkerPositions();
        this.updateVisibleRunnerMarkers();
        this.animateCameraFollow();
        this.initClusterer();

        this.updateRunnerMarkersInterval = setInterval(() => {
            this.updateRunnerMarkerPositions();
            this.updateVisibleRunnerMarkers();
            this.animateCameraFollow();
        }, 1000);

        this.favoriteRunners.subscribe(async ({favoriteRunners, currentRunnerDetail, run}) => {
            const runnersHaveChanged = !RaceMap.compareRunnersArrays(this.runners, favoriteRunners);
            const runHasChanged = this.run !== run;

            if (runHasChanged) {
                this.run = run;
                this.initClusterer();
            }

            this.runners = favoriteRunners;

            if (!this.runners) return;

            if (this.runners && !hasRunners) {
                initMap();
            }

            this.updateWaypoints();
            this.updatePaths();

            await this.matchRunnerMarkersWithRunners();
            this.updateRunnerMarkerPositions();
            this.updateVisibleRunnerMarkers();
            this.animateCameraFollow();

            if (currentRunnerDetail) {
                this.initFollowRunner(currentRunnerDetail);
            } else {
                this.setBounds();
            }

            if (runnersHaveChanged) {
                this.setBounds();
                this.initClusterer();
            }
        });
    }

    initClusterer() {
        if (this.clusterer) {
            this.clusterer.clearMarkers();
        }

        const algorithm = new SuperClusterAlgorithm({
            maxZoom: 18,
            minPoints: 2,
            radius: 5,
        });
        this.clusterer = new MarkerClusterer({
            map: this.googleMap,
            markers: [],
            algorithm,
            minimumClusterSize: 2,
            renderer: {
                render: ({count, position}) => {
                    const color = PathProvider.getPathColor(this.run);
                    const svg = window.btoa(`
                      <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                        <circle cx="120" cy="120" opacity="100" r="100" />
                      </svg>`);

                    return new google.maps.Marker({
                        position,
                        visible: count > 0,
                        icon: {
                            url: `data:image/svg+xml;base64,${svg}`,
                            scaledSize: new google.maps.Size(75, 75),
                        },
                        label: {
                            text: String(count),
                            color: 'rgba(255,255,255,1)',
                            fontSize: '16px',
                            fontWeight: 'bold',
                        },
                        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                    });
                },
            },
        });
    }

    static compareRunnersArrays(arrayA: Runner[], arrayB: Runner[]) {
        const arrayToString = (array: Runner[]) => array.map(({startNumber}) => startNumber).toString();
        return arrayToString(arrayA) === arrayToString(arrayB);
    }

    addPaths() {
        const {run: currentRun} = this.favoriteRunners.getState();

        RUNS.forEach((run) => {
            const runPath = new google.maps.Polyline({
                path: this.pathProvider.getPathPositionsForRun(run),
                strokeColor: PathProvider.getPathColor(run),
                strokeWeight: 5,
                geodesic: true,
                clickable: false,
                visible: run === currentRun,
                map: this.googleMap,
            });

            this.runPaths.set(run, runPath);
        });
    }

    updatePaths() {
        const {run} = this.favoriteRunners.getState();
        this.runPaths.forEach((path, key) => {
            path.setVisible(key === run);
        });
    }

    getWaypointsArray() {
        const {currentRunnerDetail} = this.favoriteRunners.getState();

        const currentRunner = this.runners?.find((runner) => runner.startNumber.toString() === currentRunnerDetail);
        if (currentRunner) {
            return currentRunner.waypoints;
        }

        return WAYPOINTS();
    }

    addWaypoints() {
        const {run} = this.favoriteRunners.getState();
        const waypoints = this.getWaypointsArray();

        waypoints.forEach(async (initialData) => {
            const waypoint = new Waypoint(initialData);
            const marker = new google.maps.Marker(waypoint.getMarkerOptions({
                visible: waypoint.isUsedForRun(run),
                map: this.googleMap,
                zIndex: 10,
            }));

            this.waypointMarkers.push({
                waypoint,
                marker,
            });
        });
    }

    updateWaypoints() {
        const {run} = this.favoriteRunners.getState();
        const waypoints = this.getWaypointsArray();

        this.waypointMarkers.forEach(({
            waypoint,
            marker,
        }, index) => {
            const newWaypoint = findWaypointForPosition(waypoints, waypoint.position) || waypoint;
            this.waypointMarkers[index].waypoint = newWaypoint;

            marker.setVisible(newWaypoint.isUsedForRun(run));
            marker.setIcon(newWaypoint.getMarkerIcon(run));
        });
    }

    setBounds() {
        const {run} = this.favoriteRunners.getState();
        const markers = this.pathProvider.getPathPositionsForRun(run);
        const bounds = new google.maps.LatLngBounds();
        markers.forEach((position: google.maps.LatLng) => {
            bounds.extend(position);
        });

        this.googleMap.setCenter(bounds.getCenter());
        this.googleMap.fitBounds(bounds);
    }

    initFollowRunner(startNumber: string) {
        if (this.runners && this.runnerMarkers.get(startNumber)?.getPosition()) {
            this.googleMap.setZoom(18);
            this.toggleFollowRunner();
        } else {
            this.setBounds();
        }
    }

    initGeolocationMarker() {
        const activateButton = document.querySelector('.js-race-tracking-activate-geolocation');
        if (!navigator.geolocation) {
            activateButton.parentElement.removeChild(activateButton);
        }

        activateButton.addEventListener('click', this.toggleGeolocation.bind(this));

        this.geolocationMarker = new google.maps.Marker({
            map: this.googleMap,
            icon: {
                url: `${window.config.themePath}/images/marker/geolocation.png`,
                scaledSize: new google.maps.Size(40, 40),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(20, 20),
            },
            zIndex: 101,
            visible: false,
            position: this.waypointMarkers[this.waypointMarkers.length - 1].waypoint.position,
        });
    }

    toggleGeolocation() {
        if (!navigator.geolocation) return;

        navigator.geolocation.watchPosition(
            ({coords}) => {
                this.googleMap.setZoom(16);
                this.updateGeolocationMarker({
                    lat: coords.latitude,
                    lng: coords.longitude,
                });
            },
        );

        this.showGeolocationButton = true;
        this.toggleFollow(this.FOLLOW_GEOLOCATION);
    }

    updateGeolocationMarker(position: google.maps.LatLngLiteral) {
        if (!this.geolocationMarker || !position) return;

        this.lastGeolocationPosition = position;

        if (this.showGeolocationButton) {
            this.geolocationMarker.setPosition(position);
            this.geolocationMarker.setVisible(true);
        } else {
            this.geolocationMarker.setVisible(false);
        }

        this.animateCameraFollow();
    }

    toggleFollowRunner(force?: boolean) {
        let active: boolean;

        if (force !== undefined) {
            this.toggleFollow(force ? this.FOLLOW_RUNNER : this.FOLLOW_DISABLED);
            active = force;
        } else {
            active = this.toggleFollow(this.FOLLOW_RUNNER);
        }

        this.toggleFollowRunnerButton.classList.toggle('race-tracking__toggle--active', active);
    }

    toggleFollow(follow: string): boolean {
        this.follow = this.follow === follow ? this.FOLLOW_DISABLED : follow;

        this.animateCameraFollow();

        return this.follow === follow;
    }

    animateCameraFollow() {
        const {currentRunnerDetail} = this.favoriteRunners.getState();
        if (currentRunnerDetail && this.follow === this.FOLLOW_RUNNER) {
            this.googleMap.panTo(this.runnerMarkers.get(currentRunnerDetail).getPosition());
        }

        if (this.follow === this.FOLLOW_GEOLOCATION && this.lastGeolocationPosition) {
            this.googleMap.panTo(this.lastGeolocationPosition);
        }
    }

    unsubscribe() {
        if (this.updateRunnerMarkersInterval) {
            clearInterval(this.updateRunnerMarkersInterval);
        }
    }

    updateRunnerMarkerPositions() {
        this.runners.forEach((runner) => {
            if (!runner) return;

            const runnerMarker = this.runnerMarkers.get(runner.startNumber);
            if (runnerMarker) {
                const newPosition = this.getRunnerPosition(runner);
                runnerMarker.setPosition(newPosition);
            }

            if (runner.getRunnerIsStationary()) {
                this.addMarkerToCluster(runnerMarker);
            } else {
                this.removeMarkerFromCluster(runnerMarker);
            }
        });
    }

    removeMarkerFromCluster(marker: google.maps.Marker) {
        if (this.clusterer) {
            this.clusterer.removeMarker(marker);
            marker?.setMap(this.googleMap);
        }
    }

    addMarkerToCluster(marker: google.maps.Marker) {
        if (!this.clusterer) {
            this.initClusterer();
        }

        this.clusterer.addMarker(marker);
    }

    updateVisibleRunnerMarkers() {
        this.runners.forEach((runner) => {
            if (!runner) return;

            const runnerMarker = this.runnerMarkers.get(runner.startNumber);
            if (runnerMarker) {
                runnerMarker.setVisible(this.shouldRunnerBeVisible(runner));
            }
        });
    }

    getRunnerPosition(runner: Runner): google.maps.LatLng {
        return this.pathProvider.getRunnerPositionByDistanceInterpolated(runner);
    }

    shouldRunnerBeVisible(runner: Runner): boolean {
        const {
            run: currentRun,
            currentRunnerDetail,
        } = this.favoriteRunners.getState();

        if (currentRunnerDetail !== null) {
            return currentRunnerDetail === runner.startNumber;
        }

        if (!runner.isRunning() || runner.run !== currentRun) {
            return false;
        }

        return this.favoriteRunners.getState()
            .favoriteRunners
            .some(({startNumber}) => startNumber === runner.startNumber);
    }

    matchRunnerMarkersWithRunners() {
        const removeMarkers = Array.from(this.runnerMarkers.keys())
            .filter((startNumber) => !this.runners.some((runner) => runner.startNumber === startNumber));

        removeMarkers.forEach((startNumber) => {
            const runnerMarker = this.runnerMarkers.get(startNumber);
            runnerMarker.setMap(null);
            this.runnerMarkers.delete(startNumber);
        });

        if (!this.runners) {
            return Promise.resolve();
        }

        return Promise.all(this.runners.map((runner) => {
            if (
                runner.hasDetailedData()
                && !this.runnerMarkers.get(runner.startNumber)
                && !this.runnerMarkersLock.get(runner.startNumber)
            ) {
                return this.addRunnerMarker(runner);
            }

            return Promise.resolve();
        }));
    }

    async addRunnerMarker(runner: Runner) {
        this.runnerMarkersLock.set(runner.startNumber, true);

        const {currentRunnerDetail} = this.favoriteRunners.getState();

        const isHighlighted = runner.startNumber === currentRunnerDetail;
        const icon = await RunnerIconProvider.getIcon(runner, isHighlighted);
        const runnerMarker = new google.maps.Marker({
            map: this.googleMap,
            icon,
            zIndex: 100,
            visible: this.shouldRunnerBeVisible(runner),
            position: this.getRunnerPosition(runner),
            clickable: true,
        });

        runnerMarker.addListener('click', () => {
            window.dispatchEvent(new CustomEvent('runnerClick', {
                detail: {
                    startNumber: runner.startNumber,
                },
            }));
        });

        this.runnerMarkers.set(runner.startNumber, runnerMarker);
        this.runnerMarkersLock.set(runner.startNumber, false);
    }
}

export default function initRaceMap() {
    if (!window.config || !window.config.googleMapsApiKey) {
        return Promise.reject();
    }

    return new Promise((resolve, reject) => {
        const loader = new Loader({
            apiKey: window.config.googleMapsApiKey,
            version: 'weekly',
            libraries: ['geometry'],
        });

        loader.importLibrary('maps')
            .then(async ({Map: GoogleMap}) => {
                const map = new RaceMap(GoogleMap);
                try {
                    await map.init();
                    resolve(map);
                } catch (e) {
                    reject(e);
                }
            });
    });
}
